import React, { useEffect, useMemo, useState } from 'react';
import { CheckIcon, LoaderCircleIcon, MessageCircleIcon, RotateCcwIcon } from 'lucide-react';
import type { AdminProfile } from '../../hooks/useAdminAccess';
import { CrmRecord, formatDateTime, normalizeWhatsApp, prospectId, safeErrorMessage, todayKey, value } from '../../lib/crm';
import { FollowUpService } from '../../lib/supabase/services/followUps.service';
interface FollowUpsPageProps {
  profile: AdminProfile;
}
type Tab = 'today' | 'overdue' | 'upcoming' | 'all';
export function FollowUpsPage({
  profile
}: FollowUpsPageProps) {
  const [rows, setRows] = useState<CrmRecord[]>([]);
  const [tab, setTab] = useState<Tab>('today');
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void load();
  }, []);
  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows((await FollowUpService.listPending()) as CrmRecord[]);
    } catch {
      setError('No fue posible cargar los seguimientos pendientes.');
    } finally {
      setLoading(false);
    }
  }
  const filtered = useMemo(() => {
    const today = todayKey();
    return rows.filter(row => {
      const due = dueDate(row);
      if (tab === 'all') return true;
      if (tab === 'today') return due === today;
      if (tab === 'overdue') return Boolean(due && due < today);
      return Boolean(due && due > today);
    });
  }, [rows, tab]);
  async function complete(row: CrmRecord, reschedule = false) {
    const id = value(row, 'id', 'follow_up_id');
    if (!id || submittingId) return;
    const nextDate = reschedule ? window.prompt('Nueva fecha y hora (AAAA-MM-DDTHH:mm)') : null;
    if (reschedule && !nextDate) return;
    setSubmittingId(id);
    setError(null);
    try {
      await FollowUpService.complete({
        id,
        outcome: reschedule ? 'Reprogramado' : 'Completado',
        createNext: Boolean(nextDate),
        nextDate: nextDate ? new Date(nextDate).toISOString() : null
      });
      await load();
    } catch {
      setError(safeErrorMessage());
    } finally {
      setSubmittingId('');
    }
  }
  return <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8" data-id="element-281">
      <header className="border-b border-white/10 pb-6" data-id="element-282">
        <p className="font-orbitron text-xs font-bold tracking-[.18em] text-cyan-200" data-id="element-283">
          CRM COMERCIAL
        </p>
        <h1 className="mt-2 text-3xl font-bold text-white" data-id="element-284">Seguimientos</h1>
        <p className="mt-2 text-sm text-slate-400" data-id="element-285">
          Priorizá las conversaciones pendientes según su fecha programada.
        </p>
      </header>

      <nav className="mt-6 flex gap-2 overflow-x-auto pb-1" aria-label="Filtros de seguimiento" data-id="element-286">
        {[['today', 'Hoy'], ['overdue', 'Vencidos'], ['upcoming', 'Próximos'], ['all', 'Todos']].map(([id, label]) => <button key={id} type="button" onClick={() => setTab(id as Tab)} className={`min-h-10 shrink-0 rounded-xl px-4 text-sm font-semibold ${tab === id ? 'bg-cyan-300 text-[#06202a]' : 'border border-white/10 text-slate-300'}`} data-id="element-287">
            {label}
          </button>)}
      </nav>

      {error && <p role="alert" className="mt-5 rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm text-red-100" data-id="element-288">
          {error}
        </p>}

      {loading ? <p className="mt-8 text-sm text-slate-400" data-id="element-289">Cargando seguimientos…</p> : filtered.length === 0 ? <section className="mt-6 rounded-2xl border border-dashed border-white/15 bg-[#0b1728] p-8 text-center text-sm text-slate-400" data-id="element-290">
          No hay seguimientos pendientes en esta categoría.
        </section> : <ul className="mt-6 grid gap-4 lg:grid-cols-2" data-id="element-291">
          {filtered.map((row, index) => {
        const id = value(row, 'id', 'follow_up_id');
        const prospect = prospectId(row);
        const phone = normalizeWhatsApp(value(row, 'whatsapp', 'phone'));
        const waiting = submittingId === id;
        return <li key={id || index} className="rounded-2xl border border-white/10 bg-[#0b1728] p-5" data-id="element-292">
                <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-293">
                  <div className="min-w-0" data-id="element-294">
                    <h2 className="truncate text-lg font-bold text-white" data-id="element-295">
                      {value(row, 'business_name', 'prospect_name', 'name') || 'Prospecto'}
                    </h2>
                    <p className="mt-1 text-sm text-slate-400" data-id="element-296">
                      {value(row, 'contact_name') || 'Contacto no disponible'}
                    </p>
                  </div>
                  <span className="rounded-full bg-cyan-300/10 px-2.5 py-1 text-xs font-semibold text-cyan-100" data-id="element-297">
                    {formatDateTime(value(row, 'due_at', 'scheduled_at', 'next_follow_up_at', 'follow_up_at'))}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-6 text-slate-300" data-id="element-298">
                  {value(row, 'notes', 'note', 'description', 'reason') || 'Sin nota registrada.'}
                </p>
                <div className="mt-5 flex flex-wrap gap-2" data-id="element-299">
                  {prospect && <a href={`/admin/prospectos/${prospect}`} className="inline-flex min-h-10 items-center rounded-xl border border-white/10 px-3 text-sm font-semibold text-slate-100" data-id="element-300">
                      Abrir
                    </a>}
                  {phone && <a href={`https://wa.me/${phone}`} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-emerald-300/25 px-3 text-sm font-semibold text-emerald-200" data-id="element-301">
                      <MessageCircleIcon className="h-4 w-4" data-id="element-302" /> WhatsApp
                    </a>}
                  <button type="button" disabled={waiting} onClick={() => void complete(row)} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-cyan-300 px-3 text-sm font-bold text-[#06202a] disabled:opacity-60" data-id="element-303">
                    {waiting ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-304" /> : <CheckIcon className="h-4 w-4" data-id="element-305" />}
                    Completar
                  </button>
                  <button type="button" disabled={waiting} onClick={() => void complete(row, true)} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-white/10 px-3 text-sm font-semibold text-slate-200 disabled:opacity-60" data-id="element-306">
                    <RotateCcwIcon className="h-4 w-4" data-id="element-307" /> Reprogramar
                  </button>
                </div>
              </li>;
      })}
        </ul>}
    </div>;
}
function dueDate(row: CrmRecord) {
  return value(row, 'due_at', 'scheduled_at', 'next_follow_up_at', 'follow_up_at').slice(0, 10);
}